import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type {
  InspectionStage,
  RatingSystem,
  QCCheckpoint,
  InspectionFormData,
  LocalCheckpointResult,
  OverallStatus,
} from "types/qc.types";

/**
 * QC Inspection Form Store
 * Manages the inspection form state, checkpoints, and submission
 */
class QCInspectionStore {
  rootStore: RootStore;

  // Configuration
  ratingSystem: RatingSystem = "pass_fail";
  stage: InspectionStage = "pre_pour";

  // Checkpoint definitions (loaded from config or defaults)
  checkpoints: QCCheckpoint[] = [];

  // Form data (with demo values for preview)
  formData: InspectionFormData = {
    productId: null,
    productName: "DW-4872-A Precast Double Wall Panel",
    department: "Production Line 1",
    stage: "pre_pour",
    results: {},
    overallStatus: "pending",
    notes: "",
    photos: [],
  };

  // UI State
  loading = false;
  saving = false;
  error: string | null = null;
  success: string | null = null;

  // Pre-Pour Checkpoints (default)
  prePourCheckpoints: QCCheckpoint[] = [
    { id: "form-condition", name: "Form Condition", category: "Form", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Clean, no damage or buildup", sortOrder: 1, isActive: true, createdAt: "", updatedAt: "" },
    { id: "form-joints", name: "Form Joints", category: "Form", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Properly sealed", sortOrder: 2, isActive: true, createdAt: "", updatedAt: "" },
    { id: "release-agent", name: "Release Agent", category: "Form", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Evenly applied", sortOrder: 3, isActive: true, createdAt: "", updatedAt: "" },
    { id: "reinforcing-design", name: "Reinforcing Design", category: "Reinforcing", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Matches drawings", sortOrder: 4, isActive: true, createdAt: "", updatedAt: "" },
    { id: "reinforcing-spacing", name: "Reinforcing Spacing", category: "Reinforcing", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Within tolerance", sortOrder: 5, isActive: true, createdAt: "", updatedAt: "" },
    { id: "reinforcing-cover", name: "Reinforcing Cover", category: "Reinforcing", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Adequate clearance", sortOrder: 6, isActive: true, createdAt: "", updatedAt: "" },
    { id: "lifting-devices", name: "Lifting Devices", category: "Embedded", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Properly positioned", sortOrder: 7, isActive: true, createdAt: "", updatedAt: "" },
    { id: "inserts", name: "Inserts/Plates", category: "Embedded", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Correct type and location", sortOrder: 8, isActive: true, createdAt: "", updatedAt: "" },
    { id: "blockouts", name: "Blockouts/Knockouts", category: "Embedded", checkpointType: "pass_fail", stage: "pre_pour", required: false, criteria: "If applicable", sortOrder: 9, isActive: true, createdAt: "", updatedAt: "" },
    { id: "squareness", name: "Squareness", category: "Dimensions", checkpointType: "pass_fail", stage: "pre_pour", required: true, criteria: "Within tolerance", sortOrder: 10, isActive: true, createdAt: "", updatedAt: "" },
  ];

  // Post-Pour Checkpoints (default)
  postPourCheckpoints: QCCheckpoint[] = [
    { id: "surface-finish", name: "Surface Finish", category: "Visual", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "No major defects", sortOrder: 1, isActive: true, createdAt: "", updatedAt: "" },
    { id: "cracks", name: "Cracks", category: "Visual", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "No structural cracks", sortOrder: 2, isActive: true, createdAt: "", updatedAt: "" },
    { id: "spalls", name: "Spalls", category: "Visual", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "No spalling", sortOrder: 3, isActive: true, createdAt: "", updatedAt: "" },
    { id: "honeycombing", name: "Honeycombing", category: "Visual", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "None visible", sortOrder: 4, isActive: true, createdAt: "", updatedAt: "" },
    { id: "exposed-rebar", name: "Exposed Reinforcement", category: "Visual", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "No exposed steel", sortOrder: 5, isActive: true, createdAt: "", updatedAt: "" },
    { id: "bowing", name: "Bowing", category: "Dimensions", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "Within tolerance", sortOrder: 6, isActive: true, createdAt: "", updatedAt: "" },
    { id: "product-length", name: "Product Length", category: "Dimensions", checkpointType: "measurement", stage: "post_pour", required: true, unit: "inches", sortOrder: 7, isActive: true, createdAt: "", updatedAt: "" },
    { id: "product-width", name: "Product Width", category: "Dimensions", checkpointType: "measurement", stage: "post_pour", required: true, unit: "inches", sortOrder: 8, isActive: true, createdAt: "", updatedAt: "" },
    { id: "product-depth", name: "Product Depth", category: "Dimensions", checkpointType: "measurement", stage: "post_pour", required: true, unit: "inches", sortOrder: 9, isActive: true, createdAt: "", updatedAt: "" },
    { id: "lifting-devices-post", name: "Lifting Devices", category: "Embedded", checkpointType: "pass_fail", stage: "post_pour", required: true, criteria: "Properly cast", sortOrder: 10, isActive: true, createdAt: "", updatedAt: "" },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.checkpoints = this.prePourCheckpoints;
  }

  // ============= Actions =============

  setStage = (stage: InspectionStage) => {
    this.stage = stage;
    this.formData.stage = stage;
    this.checkpoints = stage === "pre_pour" ? this.prePourCheckpoints : this.postPourCheckpoints;
    this.formData.results = {}; // Reset results when stage changes
  };

  loadProduct = async (productId: string) => {
    this.loading = true;
    this.error = null;

    try {
      const productsStore = this.rootStore.productsStore as ServiceStore;
      const product = productsStore?.entities?.find((p: any) => p.id === productId);

      // Use placeholder data for demo if product not found
      const demoProduct = {
        name: "DW-4872-A Precast Double Wall Panel",
        department: "Production Line 1",
        companyId: "demo",
      };

      // Load company QC config to get rating system
      const configStore = this.rootStore["qc-configStore"] as ServiceStore;
      if (configStore && product?.companyId) {
        await configStore.find({ query: { companyId: product.companyId } });
      }
      const config = configStore?.entities?.find((c: any) => c.companyId === product?.companyId);

      runInAction(() => {
        this.formData.productId = productId;
        this.formData.productName = product?.name || product?.productName || demoProduct.name;
        this.formData.department = product?.department || demoProduct.department;
        this.ratingSystem = config?.ratingSystem || "pass_fail";
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load product";
        this.loading = false;
      });
    }
  };

  setCheckpointResult = (checkpointId: string, result: Partial<LocalCheckpointResult>) => {
    this.formData.results[checkpointId] = {
      ...this.formData.results[checkpointId],
      checkpointId,
      ...result,
    };
    this.updateOverallStatus();
  };

  updateOverallStatus = () => {
    const requiredCheckpoints = this.checkpoints.filter((c) => c.required);
    const results = this.formData.results;

    let allPassed = true;
    let anyFailed = false;
    let allComplete = true;

    for (const checkpoint of requiredCheckpoints) {
      const result = results[checkpoint.id];

      if (!result) {
        allComplete = false;
        continue;
      }

      if (this.ratingSystem === "pass_fail") {
        if (result.passFailResult === "fail") {
          anyFailed = true;
          allPassed = false;
        } else if (result.passFailResult !== "pass") {
          allComplete = false;
        }
      } else if (this.ratingSystem === "numeric_1_5") {
        if (result.numericRating !== undefined) {
          if (result.numericRating <= 2) {
            anyFailed = true;
            allPassed = false;
          } else if (result.numericRating === 3) {
            allPassed = false; // Needs attention
          }
        } else {
          allComplete = false;
        }
      }
    }

    let status: OverallStatus;
    if (anyFailed) {
      status = "fail";
    } else if (!allComplete) {
      status = "pending";
    } else if (!allPassed) {
      status = "needs_review";
    } else {
      status = "pass";
    }

    this.formData.overallStatus = status;
  };

  addPhoto = (file: File) => {
    this.formData.photos.push(file);
  };

  removePhoto = (index: number) => {
    this.formData.photos.splice(index, 1);
  };

  setNotes = (notes: string) => {
    this.formData.notes = notes;
  };

  setFormNumber = (formNumber: string) => {
    this.formData.formNumber = formNumber;
  };

  setJobNumber = (jobNumber: string) => {
    this.formData.jobNumber = jobNumber;
  };

  setCastDate = (castDate: string) => {
    this.formData.castDate = castDate;
  };

  // ============= Submit =============

  submitInspection = async () => {
    // Validate all required checkpoints have results
    const requiredCheckpoints = this.checkpoints.filter((c) => c.required);
    const missingResults = requiredCheckpoints.filter(
      (c) => !this.formData.results[c.id]
    );

    if (missingResults.length > 0) {
      this.error = `Please complete all required checkpoints: ${missingResults.map((c) => c.name).join(", ")}`;
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const checkpointResultsStore = this.rootStore["qc-checkpoint-resultsStore"] as ServiceStore;
      const user = (this.rootStore as any).authStore?.user;

      // Create inspection record
      const inspection = await inspectionsStore.create({
        productId: this.formData.productId,
        productName: this.formData.productName,
        department: this.formData.department,
        stage: this.formData.stage,
        formNumber: this.formData.formNumber,
        jobNumber: this.formData.jobNumber,
        castDate: this.formData.castDate,
        overallStatus: this.formData.overallStatus,
        notes: this.formData.notes,
        inspectorId: user?.id,
        inspectedAt: new Date().toISOString(),
        status: "pending_approval",
      });

      // Create checkpoint results
      for (const [checkpointId, result] of Object.entries(this.formData.results)) {
        await checkpointResultsStore.create({
          inspectionId: inspection.id,
          checkpointId,
          passFailResult: result.passFailResult,
          numericRating: result.numericRating,
          statusResult: result.statusResult,
          measurementValue: result.measurementValue,
          notes: result.notes,
        });
      }

      runInAction(() => {
        this.success = "Inspection submitted for approval";
        this.saving = false;
      });

      // Navigate back after delay
      setTimeout(() => {
        this.reset();
        this.rootStore.routerStore.goTo("QCInspectorDashboard");
      }, 2000);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit inspection";
        this.saving = false;
      });
    }
  };

  // ============= Computed Values =============

  get completionPercentage(): number {
    const requiredCheckpoints = this.checkpoints.filter((c) => c.required);
    if (requiredCheckpoints.length === 0) return 100;

    const completedCount = requiredCheckpoints.filter(
      (c) => this.formData.results[c.id]
    ).length;
    return Math.round((completedCount / requiredCheckpoints.length) * 100);
  }

  get checkpointsByCategory(): Record<string, QCCheckpoint[]> {
    const grouped: Record<string, QCCheckpoint[]> = {};
    for (const checkpoint of this.checkpoints) {
      if (!grouped[checkpoint.category]) {
        grouped[checkpoint.category] = [];
      }
      grouped[checkpoint.category].push(checkpoint);
    }
    return grouped;
  }

  get isComplete(): boolean {
    const requiredCheckpoints = this.checkpoints.filter((c) => c.required);
    return requiredCheckpoints.every((c) => this.formData.results[c.id]);
  }

  get canSubmit(): boolean {
    return this.isComplete && !this.saving;
  }

  // ============= Reset =============

  reset = () => {
    this.stage = "pre_pour";
    this.checkpoints = this.prePourCheckpoints;
    this.formData = {
      productId: null,
      productName: "DW-4872-A Precast Double Wall Panel",
      department: "Production Line 1",
      stage: "pre_pour",
      results: {},
      overallStatus: "pending",
      notes: "",
      photos: [],
    };
    this.loading = false;
    this.saving = false;
    this.error = null;
    this.success = null;
  };
}

export default QCInspectionStore;
